import React, { useState } from "react";
import { IRect } from "frontend/utils/math-utils";
import { Text } from "react-konva";
import { Html } from "react-konva-utils";
import {
  getFillAndOpacity,
  ORG_CHART_NODE_TEXT_STYLES,
  ORG_CHART_NODE_TEXT_STYLES_FOR_HTML,
  OrgChartNodeTextStyles,
} from "./utils";
import { OverflowTooltipOverlay } from "./overflow-tooltip-overlay";
import { Sizes } from "frontend/data/shapes/sizes";

export function OrgchartSimpleInputField({
  id,
  initialValue,
  listening,
  isEditing,
  setIsEditing,
  rect,
  fieldTitle,
  textStyleId = "default",
  textColor,
  onFinish,
  onRealTimeChange,
}: {
  id: string;
  initialValue: string;
  listening: boolean;
  isEditing: boolean;
  setIsEditing: (state: boolean) => void;
  rect: IRect;
  fieldTitle: string;
  onFinish: (initial: string, final: string) => void;
  onRealTimeChange?: (fieldId: string, currentValue: string) => void;
  textStyleId?: OrgChartNodeTextStyles;
  textColor: string;
}) {
  let yOffset = 0;
  switch (textStyleId) {
    case "h1": {
      yOffset = 2.5;
      break;
    }
    default: {
      yOffset = 1;
      break;
    }
  }
  const { fill, opacity } = getFillAndOpacity(initialValue, textStyleId, textColor);

  const [isHovering, setIsHovering] = useState(false);

  const displayText = (initialValue || fieldTitle).trim();

  return (
    <>
      {isEditing ? (
        <Html groupProps={{ x: rect.x, y: rect.y - yOffset }}>
          <div
            style={{
              display: "grid",
              width: rect.width,
              height: rect.height,
              fontFamily: "Poppins",
            }}
          >
            <textarea
              data-testid="orgchart-input-field"
              style={{
                color: textColor,
                ...ORG_CHART_NODE_TEXT_STYLES_FOR_HTML[textStyleId],
                fontSize: `${Sizes.get().orgChart.fontSize[textStyleId]}px`,
                resize: "none",
                overflow: "hidden",
                // lineHeight now comes from ORG_CHART_NODE_TEXT_STYLES_FOR_HTML[textStyleId] - no fallback needed
              }}
              ref={(e) => e?.focus()}
              defaultValue={initialValue}
              onFocus={(e) => e.currentTarget.select()}
              onChange={(e) => {
                // Real-time update for dynamic height calculation
                if (onRealTimeChange) {
                  onRealTimeChange(id, e.currentTarget.value);
                }
              }}
              onBlur={(e) => {
                setIsEditing(false);
                onFinish(initialValue, e.currentTarget.value.trim());
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                  // Ctrl+Enter or Cmd+Enter finishes editing
                  setIsEditing(false);
                  onFinish(initialValue, e.currentTarget.value.trim());
                } else if (e.key === "Escape") {
                  setIsEditing(false);
                }
                // Regular Enter adds a new line (default behavior)
              }}
            />
          </div>
        </Html>
      ) : (
        <>
          <Text
            name={id}
            x={rect.x}
            y={rect.y}
            width={rect.width}
            height={rect.height}
            fill={fill}
            opacity={opacity}
            {...ORG_CHART_NODE_TEXT_STYLES[textStyleId]}
            fontSize={Sizes.get().orgChart.fontSize[textStyleId]}
            text={displayText}
            onClick={() => {
              setIsHovering(false);
              setIsEditing(true);
            }}
            listening={listening}
          />
          <OverflowTooltipOverlay
            isHovering={isHovering}
            setIsHovering={setIsHovering}
            rect={rect}
            displayText={displayText}
            textStyleId={textStyleId}
            fieldTitle={fieldTitle}
            suppress={isEditing}
            onDblClick={() => {
              setIsHovering(false);
              setIsEditing(true);
            }}
          />
        </>
      )}
    </>
  );
}
